import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Edit2, Save, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDetailPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchCustomer();
  }, [customerId]);

  const fetchCustomer = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers/${customerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      setCustomer(response.data);
      setFormData(response.data);
    } catch (error) {
      toast.error('Gagal memuat data customer');
      navigate('/customers/list');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/customers/${customerId}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      toast.success('Customer berhasil diupdate');
      setCustomer(formData);
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengupdate customer');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(customer);
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading customer data...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return null;
  }

  // --- SUNTIKAN FIELD BARU (Rate Code Import & Domestic) ---
  const fields = [
    { label: "Customer ID", key: "customer_id", required: true },
    { label: "Company", key: "company", required: true },
    { label: "Building", key: "building" },
    { label: "Address 1", key: "address1" },
    { label: "Address 2", key: "address2" },
    { label: "City", key: "city" },
    { label: "Post Code", key: "post_code" },
    { label: "Phone Area", key: "phone_area" },
    { label: "Phone 1", key: "phone1" },
    { label: "Phone 2", key: "phone2" },
    { label: "World Account", key: "world_acc" },
    { label: "Contact Person", key: "contact_person" },
    { label: "Rate Code (Export)", key: "rate_code" },       // Diberi keterangan Export agar jelas
    { label: "Rate Code Import", key: "rate_code_imp" },     // BARU: Input Rate Code Import
    { label: "Rate Code Domestic", key: "rate_code_dom" },   // BARU: Input Rate Code Domestic
    { label: "NPWP", key: "npwp" },
    { label: "Sales OCS", key: "sales_ocs" },
    { label: "Sales Area", key: "sales_area" },
    { label: "Date Approval", key: "date_approval" },
    { label: "Email Address", key: "email_address" },
    { label: "Currency", key: "curr" },
    { label: "City F", key: "city_f" },
    { label: "Tax Name", key: "tax_name" },
    { label: "Tax Address", key: "tax_address" },
    { label: "Bill Name", key: "bill_name" },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            onClick={() => navigate('/customers/list')}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to List
          </Button>
          <h1 className="text-4xl font-heading font-bold mb-2">
            {customer.company}
          </h1>
          <p className="text-muted-foreground text-lg">
            Customer ID: {customer.customer_id}
          </p>
        </div>
        <div>
          {!editing ? (
            <Button
              onClick={() => setEditing(true)}
              className="rounded-full font-bold shadow-hard-pop hover:shadow-hard-pop-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              data-testid="edit-customer-button"
            >
              <Edit2 className="w-5 h-5 mr-2" />
              Edit Customer
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="rounded-full font-bold shadow-hard-pop hover:shadow-hard-pop-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                data-testid="save-customer-button"
              >
                <Save className="w-5 h-5 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                onClick={handleCancel}
                variant="outline"
                className="rounded-full font-bold border-2"
              >
                <X className="w-5 h-5 mr-2" />
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Customer Details Card */}
      <Card className="border-none shadow-[0_8px_40px_rgb(0,0,0,0.08)] rounded-2xl">
        <CardHeader>
          <CardTitle className="text-2xl font-heading">
            {editing ? 'Edit Customer Information' : 'Customer Information'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((field) => {
              // Highlight khusus untuk 3 Rate Code agar mudah dicari oleh user saat mengedit
              const isRateCode = field.key.includes('rate_code');
              
              return (
              <div
                key={field.key}
                className={field.key === 'address1' || field.key === 'address2' || field.key === 'tax_address' ? 'md:col-span-2' : ''}
              >
                <Label htmlFor={field.key} className={`text-sm font-semibold mb-2 block ${isRateCode ? 'text-amber-700' : ''}`}>
                  {field.label} {field.required && '*'}
                </Label>
                {editing ? (
                  <Input
                    id={field.key}
                    name={field.key}
                    value={formData[field.key] || ''}
                    onChange={handleChange}
                    required={field.required}
                    className={`h-11 ${isRateCode ? 'border-amber-300 focus-visible:ring-amber-500' : ''}`}
                  />
                ) : (
                  <div className={`h-11 px-3 py-2 rounded-lg border flex items-center ${isRateCode ? 'bg-amber-50 border-amber-200' : 'bg-muted/30 border-border'}`}>
                    <span className={`font-medium ${isRateCode ? 'text-amber-900' : 'text-foreground'}`}>
                      {customer[field.key] || '-'}
                    </span>
                  </div>
                )}
              </div>
            )})}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerDetailPage;